import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import Checkbox from '../../components/Input/Checkbox';
import Input from '../../components/Input/Input';
import Select from '../../components/Input/Select';
import TextArea from '../../components/Input/TextArea';
import Toast from '../../components/Toast/Toast';
import ConfirmationModal from '../../components/modals/ConfirmationModal';
import { deleteProductAPI, getBrandsAPI, getCategoriesAPI, getProductByIdAPI, updateProductAPI, uploadImageAPI } from "../../utils/api";
import { BASE_URL } from "../../utils/configConstants";
import "./AddProduct.css";
import { FaArrowLeft } from "react-icons/fa";
import RichTextInput from '../../components/Input/RichTextInput';

// Helper function to get color distribution in a batch (from AddProduct)
const getColorDistribution = (batch) => {
  const distribution = {};
  batch.forEach(item => {
    distribution[item.color] = (distribution[item.color] || 0) + 1;
  });
  return distribution;
};

const EditProduct = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const filter = searchParams.get('filter') || 'all';
  const catId = searchParams.get('catId');
  // Product state
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Form state hooks - must be at top level
  const [productName, setProductName] = useState("");
  const [description, setDescription] = useState("");
  const [featured, setFeatured] = useState(false);
  const [returnPolicy, setReturnPolicy] = useState("");
  const [fabric, setFabric] = useState("");
  const [categoryIds, setCategoryIds] = useState([]);
  const [BrandId, setBrandId] = useState("");
  const [colors, setColors] = useState([{ name: "", hex_code: "" }]);
  const [sizes, setSizes] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [new_images, setNew_images] = useState([]);
  const [removedImages, setRemovedImages] = useState([]);
  const [imageColors, setImageColors] = useState([]);
  const [active, setActive] = useState(false);
  const [uploadColor, setUploadColor] = useState("");
  const handleImageUpload = (colorHex) => {
    setUploadColor(colorHex);
    document.getElementById('image-upload-input').click();
  };

  // Categories and Brands state
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingBrands, setLoadingBrands] = useState(false);

  // Delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);

  // Submit loading state
  const [submitLoading, setSubmitLoading] = useState(false);
  const [progress, setProgress] = useState(0);

  // Toast state
  const [toast, setToast] = useState({ show: false, message: "", type: "" });

  // Toast function - must be defined before useEffect hooks
  const showToast = (message, type = "error") => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: "", type: "" }), 2500);
  };



  // API call method for edit product operations
  const callEditProductAPI = async (apiFunction, requestData = null) => {
    try {
      let response;

      if (requestData) {
        response = await apiFunction(requestData);
      } else {
        response = await apiFunction();
      }

      // Check if response is valid
      if (response && typeof response === 'object') {
        return response;
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error) {
      console?.error('API call error:', error);
      throw error;
    }
  };

  // Fetch product by ID from API
  useEffect(() => {
    const fetchProduct = async () => {
      if (id) {
        try {
          setLoading(true);

          const response = await callEditProductAPI(getProductByIdAPI, id);

          if (response?.status === 200) {
            setProduct(response.data);
          } else if (response?.message) {
            setError(typeof response?.message === 'string'
              ? response?.message
              : 'Failed due to internal error');
          } else {
            setError('Failed due to internal error');
          }
        } catch (err) {
          setError('Failed due to internal error');
        } finally {
          setLoading(false);
        }
      }
    };

    fetchProduct();
  }, [id]);

  // Populate form with product data when product is loaded
  useEffect(() => {
    if (product) {
      setProductName(product?.name || "");
      setDescription(product?.description || "");
      setFeatured(product?.is_featured || false);
      setReturnPolicy(product?.return_policy || "");
      setFabric(product?.fabric || "");
      const rawCat = product?.category_id;
      let loadedCatIds = [];
      if (Array.isArray(rawCat)) {
        loadedCatIds = rawCat.map(c => String(typeof c === 'object' ? c._id : c));
      } else if (rawCat) {
        loadedCatIds = [String(rawCat)];
      }
      setCategoryIds(loadedCatIds);
      setBrandId(product?.brand_id || "");
      // Prepare colors, ensure only one 'No Color' with isNoColor: true
      let loadedColors = (product?.colors && product?.colors.length > 0)
        ? product?.colors.map(color => ({
          _id: color._id || color.hex_code || `color_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          name: color.name || "",
          hex_code: color.hex_code || ""
        }))
        : [];
      console.log(loadedColors)
      setColors(loadedColors);
      setSizes(product?.sizes || []);
      setExistingImages(product?.images || []);
      setActive(product?.active || false);

      // Set up imageColors for all images
      if (product?.images && product?.images.length > 0) {
        const initialImageColors = product.images.map(img => {
          // Try to get color from different possible properties
          let colorHex = "";

          // First try to get from color object
          if (img?.color && img?.color.hex_code) {
            colorHex = img.color.hex_code;
          }
          // Then try direct hex_code
          else if (img?.hex_code) {
            colorHex = img?.hex_code;
          }
          // Then try color_id (but we need to find the actual hex_code for this)
          else if (img?.color_id && product?.colors) {
            const colorObj = product?.colors.find(c => c._id === img?.color_id);
            if (colorObj && colorObj?.hex_code) {
              colorHex = colorObj?.hex_code;
            }
          }

          // Only return color if it was actually found, otherwise return empty string
          return colorHex || "";
        });
        setImageColors(initialImageColors);
      } else {
        // Initialize with empty array if no images
        setImageColors([]);
      }
    }
  }, [product]);



  // Fetch categories from API
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoadingCategories(true);
        const response = await callEditProductAPI(getCategoriesAPI);

        if (response?.status === 200) {
          setCategories(response.data || []);
        } else if (response?.message) {
          console?.error('Error loading categories:', typeof response?.message === 'string'
            ? response?.message
            : 'Failed due to internal error');
        } else {
          console?.error('Failed due to internal error');
        }
      } catch (err) {
        console?.error('Failed due to internal error');
      } finally {
        setLoadingCategories(false);
      }
    };

    fetchCategories();
  }, []);

  // Fetch brands from API
  useEffect(() => {
    const fetchBrands = async () => {
      try {
        setLoadingBrands(true);
        const response = await callEditProductAPI(getBrandsAPI);

        if (response?.status === 200) {
          setBrands(response.data || []);
        } else if (response?.message) {
          console.error('Error loading brands:', typeof response?.message === 'string'
            ? response?.message
            : 'Failed due to internal error');
        } else {
          console.error('Failed due to internal error');
        }
      } catch (err) {
        console.error('Failed due to internal error');
      } finally {
        setLoadingBrands(false);
      }
    };

    fetchBrands();
  }, []);



  // Show loading state
  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        Loading product...
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div style={{ textAlign: 'center', padding: '50px', color: 'red' }}>
        {error}
      </div>
    );
  }

  // Show message if no product found
  if (!product) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        Product not found
      </div>
    );
  }

  // Use fetched categories and brands data
  const categoriesData = categories;
  const BrandData = brands;

  // Handle input change for color name or hex
  const handleColorChange = (idx, field, value) => {
    const updated = colors.map((color, i) =>
      i === idx ? { ...color, [field]: value } : color
    );
    setColors(updated);
  };

  // Add a new color input row
  const handleAddMoreColor = () => {
    setColors([...colors, { _id: '', name: '', hex_code: 'rgb(0, 0, 0)' }]);
  };

  const handleRemoveColor = (idx) => {
    setColors(colors.filter((_, i) => i !== idx));
  };

  const handleSizeChange = (idx, field, value) => {
    const updated = sizes.map((row, i) =>
      i === idx ? { ...row, [field]: value } : row
    );
    setSizes(updated);
  };

  const handleAddSizeRow = () => {
    setSizes([...sizes, { _id: '', size: '', wholesale_price: '', retail_price: '', unit: '' }]);
  };

  const handleRemoveSizeRow = (idx) => {
    setSizes(sizes?.filter((_, i) => i !== idx));
  };

  // Convert file to base64
  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader?.result);
      reader.onerror = error => reject(error);
    });
  };

  // Handle image selection
  const handleImageChange = (e) => {
    const files = Array?.from(e?.target?.files);

    // Validate file types
    const allowedTypes = ['image/png', 'image/jpg', 'image/jpeg'];
    const invalidFiles = files.filter(file => !allowedTypes.includes(file.type));

    if (invalidFiles.length > 0) {
      const invalidFileNames = invalidFiles.map(file => file.name).join(', ');
      showToast(`Invalid file format(s): ${invalidFileNames}. Only PNG, JPG, and JPEG files are allowed.`, "error");
      // Reset file input
      e.target.value = '';
      return;
    }

    const processFiles = async () => {
      const new_imagesArray = [];

      for (const file of files) {
        try {
          const base64 = await convertToBase64(file);
          new_imagesArray.push({
            file,
            url: base64,
            name: `New_Image ${Date?.now()}_${Math?.random().toString(36).substr(2, 9)}`
          });
        } catch (error) {
          console.error('Error converting image to base64:', error);
          // Fallback to object URL if base64 conversion fails
          new_imagesArray?.push({
            file,
            url: URL?.createObjectURL(file),
            name: `New_Image ${Date?.now()}_${Math?.random().toString(36).substr(2, 9)}`,
            size: file.size,
            type: file.type
          });
        }
      }

      setNew_images(prev => [...prev, ...new_imagesArray]); // Append to existing array

      // Assign the color based on which section was clicked
      setImageColors(prev => [...prev, ...new Array(new_imagesArray.length).fill(uploadColor)]);
      setUploadColor(""); // Reset upload color
    };

    processFiles();

    // Reset file input value to allow selecting the same file again
    e.target.value = '';
  };

  // Handle color selection for an image
  const handleImageColorChange = (idx, value) => {
    const updated = [...imageColors];
    updated[idx] = value;
    setImageColors(updated);
  };

  // For existing images
  const handleRemoveExistingImage = (idx) => {
    const removedImage = existingImages[idx];

    // Get the current color for this image
    const currentColor = imageColors[idx];

    // Create the removed image object with proper structure
    const removedImageData = {
      _id: removedImage._id,
      image: removedImage.image,
      color_id: currentColor || removedImage.color_id || null,
      product_id: parseInt(id),
      status: "removed" // Mark as removed
    };

    setRemovedImages(prev => [...prev, removedImageData]);
    setExistingImages(existingImages.filter((_, i) => i !== idx));

    // Also remove the color selection for this image
    const updatedImageColors = [...imageColors];
    updatedImageColors.splice(idx, 1);
    setImageColors(updatedImageColors);
  };

  // For new images
  const handleRemoveNew_Image = (idx) => {
    setNew_images(new_images?.filter((_, i) => i !== idx));

    // Also remove the corresponding color entry
    const newImageIndex = existingImages.length + idx;
    setImageColors(prev => prev.filter((_, i) => i !== newImageIndex));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Set loading state
    setSubmitLoading(true);
    setProgress(0);

    // Validation checks
    if (!productName.trim()) {
      showToast("Please enter the product name.");
      setSubmitLoading(false);
      return;
    }
    if (categoryIds.length === 0) {
      showToast("Please select a category.");
      setSubmitLoading(false);
      return;
    }

    // Size validation - check for incomplete size entries
    const incompleteSizes = sizes.filter(row => {
      const hasSize = row.size?.trim();
      const hasWholesale = row.wholesale_price?.toString().trim();
      const hasRetail = row.retail_price?.toString().trim();
      const hasUnit = row.unit?.toString().trim();

      // Check if any field is filled but not all
      const filledFields = [hasSize, hasWholesale, hasRetail, hasUnit].filter(Boolean).length;
      return filledFields > 0 && filledFields < 4;
    });

    if (incompleteSizes.length > 0) {
      // Show common message for incomplete sizes
      showToast("Please complete all size fields");
      setSubmitLoading(false);
      return;
    }

    if (!sizes?.some(row => row.size && row.wholesale_price && row?.retail_price && row?.unit)) {
      showToast("Please add at least one size.");
      setSubmitLoading(false);
      return;
    }

    // Brand is optional - no validation needed
    // Color validation - check for incomplete color entries
    const colorsWithNameButNoHex = colors.filter(c => c.name?.trim() && !c.hex_code?.trim());
    const colorsWithHexButNoName = colors.filter(c => c.hex_code?.trim() && !c.name?.trim());

    if (colorsWithNameButNoHex.length > 0) {
      const colorNames = colorsWithNameButNoHex.map(c => c.name).join(', ');
      showToast(`Please enter hex code for color ${colorNames}`);
      setSubmitLoading(false);
      return;
    }

    if (colorsWithHexButNoName.length > 0) {
      const hexCodes = colorsWithHexButNoName.map(c => c.hex_code).join(', ');
      showToast(`Please enter color name for hex code ${hexCodes}`);
      setSubmitLoading(false);
      return;
    }

    // Check if at least one complete color is added
    const completeColors = colors.filter(c => c.name?.trim() && c.hex_code?.trim());
    if (completeColors.length === 0) {
      showToast("Please add at least one color with name and hex code.");
      setSubmitLoading(false);
      return;
    }

    try {

      // Prepare product data
      const productData = {
        product_id: id, // Include the product ID for update
        name: productName,
        description: description,
        is_featured: featured ? 1 : 0,
        return_policy: returnPolicy,
        fabric: fabric,
        category_id: categoryIds.map(id => parseInt(id)),
        brand_id: BrandId ? parseInt(BrandId) : null,
        colors: colors.filter(c => c.name || c.hex_code).map(c => ({
          _id: c._id || "",
          name: c.name || "",
          hex_code: c.hex_code || ""
        })),
        sizes: sizes.filter(s => s.size && s.wholesale_price && s.retail_price && s.unit).map(s => ({
          _id: s._id || "", // Include existing ID or blank for new sizes
          size: s.size,
          retail_price: parseInt(s.retail_price),
          wholesale_price: parseInt(s.wholesale_price),
          unit: parseInt(s.unit)
        })),
        active: active ? 1 : 0
      };

      //update api call
      // return
      const response = await updateProductAPI(productData);

      if (response?.status === 200) {
        // Complete progress
        setProgress(100);

        // Add a delay to ensure backend has processed the images
        showToast("Product updated successfully!", "success");
        await new Promise(resolve => setTimeout(resolve, 2000));
        // Navigate to products page with refresh parameter
        const url = catId
          ? `/product?categoryId=${catId}&refresh=true`
          : `/product?refresh=true`;
        navigate(url, { state: { filter } });
      } else if (response?.message) {
        showToast(typeof response?.message === 'string'
          ? response?.message
          : 'Failed due to internal error', "error");
      } else {
        showToast('Failed due to internal error', "error");
      }
    } catch (err) {
      showToast('Failed due to internal error', "error");
    } finally {
      setSubmitLoading(false);
      setProgress(0);
    }
  };

  const handleDeleteClick = () => {
    setProductToDelete(product);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      const reqData = {
        product_id: productToDelete?._id
      };

      const response = await callEditProductAPI(deleteProductAPI, reqData);

      if (response?.status === 200) {
        setShowDeleteModal(false);
        setProductToDelete(null);
        showToast(`Product "${productToDelete?.name}" deleted successfully!`, "success");
        const url = catId
          ? `/product?categoryId=${catId}&refresh=true`
          : `/product?refresh=true`;
        navigate(url, { state: { filter } });
      } else if (response?.message) {
        showToast(typeof response?.message === 'string'
          ? response?.message
          : 'Failed due to internal error', "error");
      } else {
        showToast('Failed due to internal error', "error");
      }
    } catch (err) {
      showToast('Failed due to internal error', "error");
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setProductToDelete(null);
  };

  // 1. Add a derived images array for display (combining existingImages and new_images)
  const images = [...existingImages, ...new_images];
  return (
    <>
      <Toast toast={toast} />
      <div className="product-main-content">
        <div className="order-details-header">
          <button className="back-button" onClick={() => {
            const url = catId
              ? `/product?categoryId=${catId}`
              : `/product`;
            navigate(url, { state: { filter } });
          }}>
            <FaArrowLeft /> Back to Products
          </button>
        </div>
        <form onSubmit={handleSubmit} className="product-form">
          <TextArea
            title="Product Name"
            value={productName}
            onChange={e => setProductName(e.target.value)}
            placeholder="Enter product name"
            required
            maxLength={255}
            rows={3}
          />

          <RichTextInput
            title="Description"
            value={description}
            onChange={setDescription}
            placeholder="Enter product description"
            rows={6}
          />

          <RichTextInput
            title="Return Policy"
            value={returnPolicy}
            onChange={setReturnPolicy}
            placeholder="Enter return policy..."
            rows={6}
          />

          <Input
            title="Fabric"
            type="text"
            value={fabric}
            onChange={e => setFabric(e.target.value)}
            placeholder="Enter fabric type (e.g. Cotton, Silk)"
            maxLength={255}
          />

          <div className="cat-list-section">
            <div className="cat-list-header">
              <h3 className="cat-list-title">Category</h3>
              <label className="cat-list-item cat-select-all">
                <input
                  type="checkbox"
                  checked={categoriesData.length > 0 && categoryIds.length === categoriesData.length}
                  onChange={() => {
                    if (categoryIds.length === categoriesData.length) setCategoryIds([]);
                    else setCategoryIds(categoriesData.map(c => String(c._id)));
                  }}
                />
                <span>Select All</span>
              </label>
            </div>
            {loadingCategories ? (
              <p className="cat-list-loading">Loading categories...</p>
            ) : (
              <div className="cat-list-grid">
                {categoriesData.map(cat => {
                  const id = String(cat._id);
                  const checked = categoryIds.includes(id);
                  return (
                    <label key={id} className={`cat-list-item${checked ? ' cat-list-item--checked' : ''}`}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => setCategoryIds(prev =>
                          prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
                        )}
                      />
                      <span>{cat.name}</span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          <Select
            title="Brand"
            value={BrandId}
            onChange={e => setBrandId(e.target.value)}
            placeholder={loadingBrands ? "Loading brands..." : "Select Brand"}
            disabled={loadingBrands}
            options={BrandData.map(brand => ({ value: brand._id, label: brand.name }))}
          />

          <Checkbox
            title="Featured"
            checked={featured}
            onChange={e => setFeatured(e.target.checked)}
          />

          <div className="size-section">
            <h3 className="size-title">Size</h3>
            <div className="size-header">
              <div className="size-header-item">SIZE</div>
              <div className="size-header-item">WHOLESALE PRICE</div>
              <div className="size-header-item">RETAIL PRICE</div>
              <div className="size-header-item">UNIT</div>
              <div className="remove-price-header-placeholder"></div>
            </div>
            {sizes.map((row, idx) => (
              <div className="price-row" key={idx}>
                <input
                  type="text"
                  className="size-textarea"
                  placeholder="Size"
                  value={row.size}
                  onChange={e => handleSizeChange(idx, "size", e.target.value)}
                />
                <input
                  type="text"
                  className="price-input"
                  placeholder="Wholesale Price"
                  value={row.wholesale_price}
                  onChange={e => handleSizeChange(idx, "wholesale_price", e.target.value)}
                  maxLength={8}
                  inputMode="numeric"
                  pattern="[0-9]*"
                />
                <input
                  type="text"
                  className="price-input"
                  placeholder="Retail Price"
                  value={row.retail_price}
                  onChange={e => handleSizeChange(idx, "retail_price", e.target.value)}
                  maxLength={8}
                  inputMode="numeric"
                  pattern="[0-9]*"
                />
                <input
                  type="text"
                  className="price-input"
                  placeholder="Unit"
                  value={row.unit}
                  onChange={e => handleSizeChange(idx, "unit", e.target.value)}
                  maxLength={8}
                  inputMode="numeric"
                  pattern="[0-9]*"
                />
                {sizes.length > 1 ? (
                  <button
                    type="button"
                    className="remove-price-btn"
                    onClick={() => handleRemoveSizeRow(idx)}
                    title="Delete"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                      strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="3 6 5 6 21 6"></polyline>
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"></path>
                      <line x1="10" y1="11" x2="10" y2="17"></line>
                      <line x1="14" y1="11" x2="14" y2="17"></line>
                    </svg>
                  </button>
                ) : (
                  <div className="remove-price-header-placeholder"></div>
                )}
              </div>
            ))}
            <div className="add-price-btn-container">
              <button
                type="button"
                className="add-price-btn"
                onClick={handleAddSizeRow}
                title="Add Size"
              >
                Add More
              </button>
            </div>
          </div>
          {/* Color Section */}
          <div className="size-section">
            <h3 className="size-title">Color</h3>
            <div className="size-header">
              <div className="size-header-item">COLOR NAME</div>
              <div className="size-header-item">COLOR PICKER</div>
              <div className="size-header-item">HEX CODE</div>
              <div className="remove-size-header-placeholder"></div>
            </div>
            {colors.map((color, idx) => (
              <div className="price-row" key={idx}>
                <input
                  type="text"
                  placeholder="Color name"
                  value={color.name}
                  onChange={e => handleColorChange(idx, "name", e.target.value)}
                  className="size-textarea"
                  disabled={color.isNoColor}
                />
                <div className="color-picker-input-container">
                  <input
                    type="color"
                    value={color.hex_code === "rgba(0, 0, 0, 0)" ? "#000000" : color.hex_code || "#000000"}
                    onChange={e => handleColorChange(idx, "hex_code", e.target.value)}
                    className="color-picker-input"
                    disabled={color.isNoColor}
                  />
                </div>
                <input
                  type="text"
                  placeholder="Hex code"
                  value={color.hex_code}
                  onChange={e => handleColorChange(idx, "hex_code", e.target.value)}
                  className="size-textarea"
                  maxLength={7}
                  disabled={color.isNoColor}
                />
                {!color.isNoColor && colors.length > 1 ? (
                  <button
                    type="button"
                    className="remove-price-btn"
                    onClick={() => handleRemoveColor(idx)}
                    title="Delete"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                      strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="3 6 5 6 21 6"></polyline>
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"></path>
                      <line x1="10" y1="11" x2="10" y2="17"></line>
                      <line x1="14" y1="11" x2="14" y2="17"></line>
                    </svg>
                  </button>
                ) : (
                  <div className="remove-color-btn-placeholder"></div>
                )}
              </div>
            ))}
            <div className="add-size-btn-container">
              <button
                type="button"
                onClick={handleAddMoreColor}
                className="add-price-btn"
                title="Add Color"
              >
                Add More
              </button>
            </div>
          </div>

          <div className="submit-btn-row">
            <button
              type="submit"
              className="submit-btn"
              disabled={submitLoading}
            >
              {submitLoading ? "Updating..." : "Update Product"}
            </button>
          </div>
        </form>

        {/* Delete Confirmation Modal */}
        <ConfirmationModal
          visible={showDeleteModal}
          onConfirm={handleDeleteConfirm}
          onCancel={handleDeleteCancel}
          title="Delete Product"
          message={`Are you sure you want to delete "${productToDelete?.name}"?`}
          confirmText="Delete"
        />
      </div>
    </>
  );
};

export default EditProduct; 